import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, User, ArrowRight, Check, Home } from 'lucide-react';
import {
  isAdminAuthenticated,
  loginAdmin,
  getRememberedCredentials,
  saveRememberedCredentials,
  clearRememberedCredentials,
} from '@/lib/adminAuth';
import { getAdminBase, realmForBase } from '@/lib/adminBase';
import { brandForRealm, isWhiteLabel } from '@/lib/adminBrand';

const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const base = getAdminBase(location.pathname);
  const realm = realmForBase(base);
  const brand = brandForRealm(realm);
  const light = isWhiteLabel(realm);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // /albaniaadmin and /romaniaadmin share the same requested tab title; the
    // /socialschool white-label keeps its own branded title.
    document.title =
      base === '/socialschool'
        ? `${brand.portalName} — Sign In`
        : 'Zera Romania - SocialaPop.ai';
    if (isAdminAuthenticated(realm)) {
      navigate(`${base}/dashboard`, { replace: true });
      return;
    }
    // Pre-fill saved credentials if the user chose to remember them.
    const saved = getRememberedCredentials(realm);
    if (saved) {
      setUsername(saved.username);
      setPassword(saved.password);
      setRemember(true);
    }
  }, [navigate, base, realm, brand.portalName]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    // Small delay for UX
    setTimeout(() => {
      if (loginAdmin(username.trim(), password, realm)) {
        if (remember) {
          saveRememberedCredentials(username.trim(), password, realm);
        } else {
          clearRememberedCredentials(realm);
        }
        navigate(`${base}/dashboard`, { replace: true });
      } else {
        setError('Invalid credentials. Please try again.');
        setLoading(false);
      }
    }, 300);
  };

  // Theme tokens: Zera realms keep the dark navy skin; the Lewith & Freeman
  // white-label uses a light enterprise skin with blue + gold accents.
  const t = light
    ? {
        page: 'min-h-screen bg-[#F6F8FB] flex items-center justify-center px-6',
        iconWrap: 'w-16 h-16 rounded-2xl bg-[#1E4E8C]/5 border border-[#1E4E8C]/20 flex items-center justify-center mx-auto mb-6',
        icon: 'w-6 h-6 text-[#1E4E8C]',
        eyebrow: 'font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961] block mb-3',
        title: 'font-serif text-3xl text-[#12263F] font-light mb-2',
        sub: 'font-sans text-xs text-[#12263F]/50',
        form: 'bg-white border border-[#12263F]/10 rounded-2xl shadow-sm p-8 space-y-6',
        label: 'font-sans text-[10px] tracking-[0.25em] uppercase text-[#12263F]/50 block mb-2',
        inputIcon: 'w-4 h-4 text-[#12263F]/30 absolute left-3 top-1/2 -translate-y-1/2',
        input:
          'w-full bg-[#F6F8FB] border border-[#12263F]/15 rounded-lg pl-10 pr-4 py-3 font-sans text-sm text-[#12263F] placeholder:text-[#12263F]/30 focus:outline-none focus:border-[#1E4E8C] transition-colors',
        checkOn: 'bg-[#1E4E8C] border-[#1E4E8C]',
        checkOff: 'bg-white border-[#12263F]/20 group-hover:border-[#1E4E8C]/50',
        checkMark: 'w-3 h-3 text-white',
        checkText: 'font-sans text-[11px] tracking-wide text-[#12263F]/60 group-hover:text-[#12263F]/80 transition-colors',
        button:
          'w-full font-sans text-[10px] tracking-[0.3em] uppercase px-8 py-4 rounded-lg bg-[#1E4E8C] text-white hover:bg-[#173d70] transition-colors flex items-center justify-center gap-2 disabled:opacity-50',
        footer: 'text-center font-sans text-[10px] text-[#12263F]/30 mt-8 tracking-wider',
        error: 'bg-red-50 border border-red-200 rounded-lg px-4 py-3',
        errorText: 'font-sans text-xs text-red-600',
      }
    : {
        page: 'min-h-screen bg-[#0D1B2A] flex items-center justify-center px-6',
        iconWrap: 'w-16 h-16 rounded-full bg-[#C9A961]/10 border border-[#C9A961]/30 flex items-center justify-center mx-auto mb-6',
        icon: 'w-6 h-6 text-[#C9A961]',
        eyebrow: 'font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961] block mb-3',
        title: 'font-serif text-3xl text-white font-light mb-2',
        sub: 'font-sans text-xs text-white/40',
        form: 'bg-white/[0.03] border border-white/10 backdrop-blur-sm p-8 space-y-6',
        label: 'font-sans text-[10px] tracking-[0.25em] uppercase text-white/50 block mb-2',
        inputIcon: 'w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2',
        input:
          'w-full bg-white/[0.04] border border-white/10 pl-10 pr-4 py-3 font-sans text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#C9A961] transition-colors',
        checkOn: 'bg-[#C9A961] border-[#C9A961]',
        checkOff: 'bg-white/[0.04] border-white/20 group-hover:border-[#C9A961]/50',
        checkMark: 'w-3 h-3 text-[#0D1B2A]',
        checkText: 'font-sans text-[11px] tracking-wide text-white/60 group-hover:text-white/80 transition-colors',
        button:
          'w-full font-sans text-[10px] tracking-[0.3em] uppercase px-8 py-4 bg-[#C9A961] text-[#0D1B2A] hover:bg-[#b8944d] transition-colors flex items-center justify-center gap-2 disabled:opacity-50',
        footer: 'text-center font-sans text-[10px] text-white/20 mt-8 tracking-wider',
        error: 'bg-red-500/10 border border-red-500/30 px-4 py-3',
        errorText: 'font-sans text-xs text-red-300',
      };

  const HeadIcon = light ? Home : Lock;

  return (
    <div className={t.page}>
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className={t.iconWrap}>
            <HeadIcon className={t.icon} />
          </div>
          <span className={t.eyebrow}>{brand.loginEyebrow}</span>
          <h1 className={t.title}>{brand.portalName}</h1>
          <p className={t.sub}>Authorized personnel only. All access is logged.</p>
        </div>

        <form onSubmit={handleSubmit} className={t.form}>
          <div>
            <label className={t.label}>Username</label>
            <div className="relative">
              <User className={t.inputIcon} />
              <input
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={t.input}
                placeholder="Enter username"
                required
              />
            </div>
          </div>

          <div>
            <label className={t.label}>Password</label>
            <div className="relative">
              <Lock className={t.inputIcon} />
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={t.input}
                placeholder="Enter password"
                required
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => setRemember((v) => !v)}
            className="flex items-center gap-3 group"
          >
            <span
              className={`w-4 h-4 border flex items-center justify-center transition-colors ${
                remember ? t.checkOn : t.checkOff
              }`}
            >
              {remember && <Check className={t.checkMark} strokeWidth={3} />}
            </span>
            <span className={t.checkText}>Remember my login credentials</span>
          </button>

          {error && (
            <div className={t.error}>
              <p className={t.errorText}>{error}</p>
            </div>
          )}

          <button type="submit" disabled={loading} className={t.button}>
            {loading ? 'Signing In...' : (
              <>
                Sign In <ArrowRight className="w-3.5 h-3.5" />
              </>
            )}
          </button>
        </form>

        <p className={t.footer}>{brand.loginFooter}</p>
      </div>
    </div>
  );
};

export default AdminLogin;
